import { z } from "zod";
import {
  DraftQuotePushConfigSchema,
  ERP_SYNC_CONFIG_PROPERTY_ORDER,
  ERPSyncConfigSchema,
  OpportunityCustomFieldMappingSchema,
} from "./schemas";

/**
 * JobBOSS² quote header fields a custom field mapping may target. `QuoteCreate`
 * and `QuoteUpdate` also accept fields this integration owns outright (customer,
 * currency, exchange rate, addresses), so only the ones a user may legitimately
 * steer are listed here. Date targets take a date custom field's `yyyy-MM-dd`
 * value, written at midnight in the organization's timezone.
 */
export const JOBBOSS2_MAPPABLE_QUOTE_FIELDS = [
  "salesID",
  "termsCode",
  "taxCode",
  "territory",
  "shipVia",
  "shippingCode",
  "GSTCode",
  "followUpDate",
  "user_Text1",
  "user_Text2",
  "user_Text3",
  "user_Text4",
  "user_Memo1",
  "user_Number1",
  "user_Number2",
  "user_Number3",
  "user_Number4",
  "user_Date1",
  "user_Date2",
  "user_Currency1",
  "user_Currency2",
] as const;

/** Mapping target that fills a text custom field with the JobBOSS² quote number after a push. */
export const JOBBOSS2_QUOTE_NUMBER_TARGET = "quoteNo";

/** Mapping target that fills a text company custom field with the JobBOSS² customer code after a push or pull. */
export const JOBBOSS2_CUSTOMER_CODE_TARGET = "customerCode";

/** A JobBOSS² quote header field a custom field mapping may target. */
export type JobBoss2MappableQuoteField =
  (typeof JOBBOSS2_MAPPABLE_QUOTE_FIELDS)[number];

/**
 * Per-customer JobBOSS² sync configuration. Stored as `integrationConfig` jsonb
 * on the `erpIntegrationSyncsTable` row. Extends the shared
 * {@link ERPSyncConfigSchema} with the JobBOSS²-specific knobs.
 */
export const JobBoss2SyncConfigSchema = ERPSyncConfigSchema.extend({
  ...DraftQuotePushConfigSchema.shape,
  quoteCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Quote custom field mapping",
    description:
      "Map Forgepoint quote custom fields to JobBOSS² quote header fields, for example a Sales ID field to salesID.",
  }),
  companyCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Company custom field mapping",
    description:
      "Map Forgepoint company custom fields to JobBOSS² customer fields, for example a Customer Code field to the customer code.",
  }),
  overwriteQuoteOnRevision: z.boolean().default(false).meta({
    title: "Overwrite the JobBOSS² quote on a new revision",
    description:
      "Update the existing JobBOSS² quote in place when a new Forgepoint revision is finalized, instead of creating a second JobBOSS² quote.",
  }),
  enableIntegrationLookup: z.boolean().default(false).meta({
    title: "Agent read access to JobBOSS²",
    description:
      "Let the agent look up records in JobBOSS², such as actual job hours, with read-only requests.",
  }),
  quotedByEmployeeByUserId: z.record(z.string(), z.string()).optional().meta({
    title: "Employee mapping",
    description:
      "Map each Forgepoint user to their JobBOSS² employee. Quoted By is the first estimator on the quote's opportunity. Account Manager is the quote's sales rep.",
  }),
  defaultPurchasedVendorCode: z.string().min(1).optional().meta({
    title: "Default purchased vendor",
    description:
      "JobBOSS² vendor set on estimates created for purchased items, so their cost breaks are written as purchase costs. Required to push purchased items.",
  }),
});

/** Per-customer JobBOSS² sync configuration. */
export type JobBoss2SyncConfig = z.infer<typeof JobBoss2SyncConfigSchema>;

/** Display order for the JobBOSS² sync config properties. */
export const JOBBOSS2_SYNC_CONFIG_PROPERTY_ORDER = [
  ...ERP_SYNC_CONFIG_PROPERTY_ORDER,
  "pushDraftQuotes",
  "overwriteQuoteOnRevision",
  "enableIntegrationLookup",
  "defaultPurchasedVendorCode",
  "quoteCustomFieldMapping",
  "companyCustomFieldMapping",
  "quotedByEmployeeByUserId",
];
